// calendar.js

let calendarAbortController = new AbortController();

// ─── CONSTANTS ───────────────────────────────────────────────────────────────
const CELL_H = 28;
const HEADER_H = 56;
const BAR_H = 4;
const CELL_PAD = 2;
const RADIUS = 3;
const COL = 7;

const MONTHS = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',];

const COLOR_SCALE = ['#0d1117', '#162032', '#1a2f4e', '#1b4566', '#165c7a', '#137a8a', '#2a9d8f', '#52b788', '#90c96a', '#d4c848', '#fde68a',];

// ─── ENTRY POINT ─────────────────────────────────────────────────────────────
/**
 * Fetches calendar data and re-renders all `[data-calendar]` containers.
 * Mirrors the updateCharts(date) pattern from charts.js.
 */
const updateCalendar = (date) => {
  calendarAbortController.abort();
  calendarAbortController = new AbortController();
  const signal = calendarAbortController.signal;

  document.querySelectorAll('[data-calendar]').forEach(calendarEl => {
    const url = calendarEl.getAttribute('data-calendar');
    if (!url) return;

    calendarEl.classList.add('is-loading');

    fetch(`${url}?date=${date}`, {signal})
      .then(response => {
        if (!response.ok) throw new Error('HTTP error ' + response.status);
        return response.json();
      })
      .then(json => {
        const year = parseInt(date.slice(0, 4), 10);
        // date       = vom dateChange-Event geliefertes Datum (Obergrenze der Darstellung).
        // json.pivot = letzter Tag mit echten Daten — kann davor liegen.
        // Platzhalter werden für alle Tage von json.pivot+1 bis date gerendert.
        const days = json.days ?? {};
        const maxProd = Object.values(days).reduce((max, v) => Math.max(max, v.productionReported ?? 0), 1,);
        const layout = buildLayout(year, date);

        _updateStats(calendarEl, days);
        _renderLegend(calendarEl);
        _render(calendarEl, layout, days, maxProd);

        // ResizeObserver-State aktualisieren, damit nach einem Fetch
        // der nächste Resize mit den neuen Daten rendert.
        _updateResizeState(calendarEl, layout, days, maxProd);
      })
      .catch(e => {
        if (e.name === 'AbortError') return;
        console.error('Calendar fetch error:', e);
      })
      .finally(() => {
        if (!signal.aborted) {
          calendarEl.classList.remove('is-loading');
        }
      });
  });
};

document.addEventListener('dateChange', (e) => {
  updateCalendar(e.detail.date);
});

// ─── LAYOUT BUILDER ──────────────────────────────────────────────────────────
/**
 * Builds a flat list of rows (month-header | week)
 * covering 1 Jan to 31 Dec of `year`, with padding weeks at the start.
 */
export function buildLayout (year, pivotStr) {
  // pivot = last date to show (chosen date from dateChange event)
  const pivot = new Date(pivotStr + 'T23:59:59');

  const jan1 = new Date(year, 0, 1);
  const startDow = (jan1.getDay() + 6) % 7; // 0 = Monday
  const cur = new Date(jan1);
  cur.setDate(jan1.getDate() - startDow);

  const rows = [];
  let lastMonth = -1;

  while (true) {
    const weekDays = Array.from({length: COL}, () => {
      const d = new Date(cur);
      cur.setDate(cur.getDate() + 1);
      return d;
    });

    if (weekDays[6] < jan1) continue;                // week entirely before year
    if (weekDays[0] > new Date(year, 11, 31)) break; // past Dec 31

    // Insert month header before any week that contains the 1st of a new month
    const inYear = weekDays.filter(d => d.getFullYear() === year);
    const newMonths = [...new Set(inYear.map(d => d.getMonth()))].filter(m => m !== lastMonth);

    for (const m of newMonths) {
      const has1st = inYear.some(d => d.getMonth() === m && d.getDate() === 1);
      if (has1st || (m === 0 && lastMonth === -1)) {
        rows.push({type: 'month-header', month: m, year});
        lastMonth = m;
      }
    }

    rows.push({type: 'week', days: weekDays});

    if (inYear.some(d => d.getMonth() === 11 && d.getDate() === 31)) break;
  }

  return {rows, year, pivot};
}

// ─── SVG RENDERER ────────────────────────────────────────────────────────────
function _render (calendarEl, layout, data, maxProd) {
  const svgContainer = calendarEl.querySelector('[data-calendar-svg]');
  if (!svgContainer) return;

  const {rows, year, pivot} = layout;
  const w = svgContainer.clientWidth;
  const cellW = w / COL;
  const svgNS = 'http://www.w3.org/2000/svg';

  // Single pass: compute Y positions
  let svgH = 0;
  const rowY = rows.map(row => {
    const y = svgH;
    if (row.type === 'month-header') svgH += HEADER_H; else svgH += CELL_H + CELL_PAD;
    return y;
  });
  svgH += 8;

  const svg = document.createElementNS(svgNS, 'svg');
  svg.setAttribute('width', w);
  svg.setAttribute('height', svgH);
  svg.setAttribute('viewBox', `0 0 ${w} ${svgH}`);

  const hitTargets = [];

  rows.forEach((row, ri) => {
    const y = rowY[ri];
    if (row.type === 'month-header') _renderMonthHeader(svg, row, y, w, data, svgNS); else if (row.type === 'week') _renderWeekRow(svg, row, y, cellW, year, pivot, data, maxProd, hitTargets, svgNS);
  });

  svgContainer.innerHTML = '';
  svgContainer.appendChild(svg);

  _bindTooltip(calendarEl, hitTargets);
}

// ── Month header ──────────────────────────────────────────────────────────────
function _renderMonthHeader (svg, row, y, w, data, svgNS) {
  // ── Month name ──
  const t = document.createElementNS(svgNS, 'text');
  t.setAttribute('x', w / 2);
  t.setAttribute('y', y + 18);
  t.setAttribute('text-anchor', 'middle');
  t.setAttribute('fill', '#9ca3af');
  t.setAttribute('font-size', '13');
  t.setAttribute('letter-spacing', '0.04em');
  t.textContent = `${MONTHS[row.month]} ${row.year}`;
  svg.appendChild(t);

  // ── Monthly stats ──
  // Collect all days belonging to this month
  const prefix = `${row.year}-${String(row.month + 1).padStart(2, '0')}-`;
  const vals = Object.entries(data)
    .filter(([k]) => k.startsWith(prefix))
    .map(([, v]) => v);

  if (!vals.length) return;

  const total = vals.reduce((s, v) => s + (v.productionReported ?? 0), 0);
  const avg = total / vals.length;
  const quotaVals = vals.filter(v => v.quota !== null && v.quota !== undefined);
  const quota = quotaVals.length ? quotaVals.reduce((s, v) => s + v.quota, 0) / quotaVals.length : null;

  // Four stats in a single line, evenly spaced
  const stats = [{label: 'Produktion', value: _formatEnergy(total)}, {
    label: 'Ø pro Tag', value: `${_formatNumber(avg, 1)} kWh`
  }, {label: 'Ø Eigenverbr.', value: quota !== null ? `${_formatNumber(quota, 0)} %` : '—'}, {
    label: 'Tage', value: `${vals.length}`
  },];

  const colW = w / stats.length;
  stats.forEach(({label, value}, i) => {
    const cx = colW * i + colW / 2;

    const lbl = document.createElementNS(svgNS, 'text');
    lbl.setAttribute('x', cx);
    lbl.setAttribute('y', y + 32);
    lbl.setAttribute('text-anchor', 'middle');
    lbl.setAttribute('fill', '#6b7280');
    lbl.setAttribute('font-size', '9');
    lbl.textContent = label;
    svg.appendChild(lbl);

    const val = document.createElementNS(svgNS, 'text');
    val.setAttribute('x', cx);
    val.setAttribute('y', y + 46);
    val.setAttribute('text-anchor', 'middle');
    val.setAttribute('fill', '#d1d5db');
    val.setAttribute('font-size', '10');
    val.setAttribute('font-weight', '500');
    val.textContent = value;
    svg.appendChild(val);
  });
}

// ── Week row ──────────────────────────────────────────────────────────────────
function _renderWeekRow (svg, row, y, cellW, year, pivot, data, maxProd, hitTargets, svgNS) {
  row.days.forEach((d, i) => {
    if (d.getFullYear() !== year) return; // padding days outside year

    const x = i * cellW;
    const key = _formatKey(d);
    const dayData = data[key];

    // Tage nach dem Pivot: nicht rendern
    if (d > pivot) return;

    // Tage vor/gleich Pivot ohne Daten: Platzhalter (kein Balken, keine Interaktion)
    const isPlaceholder = !dayData;

    const bgColor = isPlaceholder ? '#161b22' : _productionColor(dayData.productionReported ?? 0, maxProd);
    const lum = isPlaceholder ? 0 : _luminance(bgColor);
    const textColor = isPlaceholder ? '#2d3748' : (lum > 0.35 ? '#111' : lum > 0.15 ? '#ddd' : '#6b7280');
    const barW = isPlaceholder ? 0 : ((dayData.quota ?? 0) / 100) * (cellW - CELL_PAD * 2);

    // Cell background
    const rect = document.createElementNS(svgNS, 'rect');
    rect.setAttribute('x', x + CELL_PAD);
    rect.setAttribute('y', y + CELL_PAD);
    rect.setAttribute('width', cellW - CELL_PAD * 2);
    rect.setAttribute('height', CELL_H - CELL_PAD * 2);
    rect.setAttribute('rx', RADIUS);
    rect.setAttribute('fill', bgColor);
    svg.appendChild(rect);

    // Eigenverbrauchsquote bar (bottom edge)
    if (barW > 0) {
      const bar = document.createElementNS(svgNS, 'rect');
      bar.setAttribute('x', x + CELL_PAD);
      bar.setAttribute('y', y + CELL_H - CELL_PAD - BAR_H);
      bar.setAttribute('width', barW);
      bar.setAttribute('height', BAR_H);
      bar.setAttribute('rx', 1);
      bar.setAttribute('fill', textColor);
      bar.setAttribute('fill-opacity', '0.45');
      svg.appendChild(bar);
    }

    // Day number
    const t = document.createElementNS(svgNS, 'text');
    t.setAttribute('x', x + cellW / 2);
    t.setAttribute('y', y + CELL_H / 2 + 4);
    t.setAttribute('text-anchor', 'middle');
    t.setAttribute('fill', textColor);
    t.setAttribute('font-size', '10');
    t.setAttribute('font-weight', dayData ? '500' : '300');
    t.textContent = d.getDate();
    svg.appendChild(t);

    // Invisible hit area for tooltip (only when data exists)
    if (dayData) {
      const hit = document.createElementNS(svgNS, 'rect');
      hit.setAttribute('x', x);
      hit.setAttribute('y', y);
      hit.setAttribute('width', cellW);
      hit.setAttribute('height', CELL_H);
      hit.setAttribute('fill', 'transparent');
      hit.setAttribute('cursor', 'pointer');
      hit.style.touchAction = 'manipulation';
      hit.dataset.date = d.toLocaleDateString('de-CH', {weekday: 'long', day: 'numeric', month: 'long'});
      hit.dataset.pvToConsumer = (dayData.pvToConsumer ?? 0).toFixed(1);
      hit.dataset.pvToGrid = (dayData.pvToGrid ?? 0).toFixed(1);
      hit.dataset.quota = (dayData.quota ?? 0).toFixed(1);
      hit.dataset.productionReported = (dayData.productionReported ?? 0).toFixed(1);
      svg.appendChild(hit);
      hitTargets.push(hit);
    }
  });
}

// ─── TOOLTIP ─────────────────────────────────────────────────────────────────
//
// Design decisions:
//   - Click/tap to show, click/tap-outside to hide — scroll is never blocked
//   - Click/tap to toggle; click outside closes
//   - Single global tooltip element shared across all calendar instances
//   - Magnet positioning: prefers below-right of cell, flips to whichever
//     quadrant has the most room (anchor = cell centre, not cursor position)
//   - Viewport clamping after quadrant choice
//   - GPU-friendly: position via transform: translate3d(), left/top fixed at 0

// One global tooltip, lazily created and appended to <body>.
let _globalTooltip = null;
let _globalTooltipActiveHit = null; // currently shown hit element

function _getOrCreateGlobalTooltip () {
  if (_globalTooltip) return _globalTooltip;

  const el = document.createElement('div');
  el.setAttribute('data-calendar-tooltip', '');
  el.className = 'calendar-tooltip';
  // GPU layer: position is controlled entirely via transform
  el.style.cssText = 'position:fixed;top:0;left:0;will-change:transform;';
  document.body.appendChild(el);
  _globalTooltip = el;

  // Hide on click outside — works on both desktop and mobile.
  // Using click (not mousedown/touchstart) avoids the dual-event race on mobile
  // where touchstart + synthetic click would show then immediately hide the tooltip.
  document.addEventListener('click', e => {
    if (!_globalTooltipActiveHit) return;
    if (e.target.closest('[data-calendar-tooltip]')) return;
    if (e.target.dataset && e.target.dataset.date) return;
    _hideTooltip();
  }, {passive: true});

  // Reposition on scroll/resize — rAF-throttled to avoid layout thrashing.
  let _raf = null;
  const reposition = () => {
    if (!_globalTooltipActiveHit || _raf) return;
    _raf = requestAnimationFrame(() => {
      _raf = null;
      if (_globalTooltipActiveHit) _positionTooltip(_globalTooltipActiveHit);
    });
  };
  window.addEventListener('scroll', reposition, {passive: true, capture: true});
  window.addEventListener('resize', reposition, {passive: true});
  window.addEventListener('orientationchange', reposition, {passive: true});

  return _globalTooltip;
}

function _showTooltip (hit) {
  const tooltip = _getOrCreateGlobalTooltip();
  _globalTooltipActiveHit = hit;

  const pvCalc = +hit.dataset.pvToConsumer + +hit.dataset.pvToGrid;
  tooltip.innerHTML = `
    <p class="calendar-tooltip-date">${hit.dataset.date}</p>
    <div class="calendar-tooltip-row">
      <span>Produktion (berechnet)</span>
      <span>${_formatNumber(pvCalc, 1)} kWh</span>
    </div>
    <div class="calendar-tooltip-row">
      <span>&#x2192; Verbraucher</span>
      <span>${_formatNumber(+hit.dataset.pvToConsumer, 1)} kWh</span>
    </div>
    <div class="calendar-tooltip-row">
      <span>&#x2192; Einspeisung</span>
      <span>${_formatNumber(+hit.dataset.pvToGrid, 1)} kWh</span>
    </div>
    <div class="calendar-tooltip-row">
      <span>Eigenverbrauch</span>
      <span>${_formatNumber(+hit.dataset.quota, 1)} %</span>
    </div>
    <div class="calendar-tooltip-row calendar-tooltip-reported">
      <span>Produktion (SolarManager)</span>
      <span>${_formatNumber(+hit.dataset.productionReported, 1)} kWh</span>
    </div>
  `;

  // Position before making visible to avoid a frame at 0,0
  _positionTooltip(hit);
  tooltip.classList.add('is-active');
}

function _hideTooltip () {
  if (_globalTooltip) _globalTooltip.classList.remove('is-active');
  _globalTooltipActiveHit = null;
}

/**
 * Magnet positioning anchored to the centre of the hit element.
 * Tries all four quadrants and picks the one with the most available space.
 * Clamps to viewport with MARGIN padding on all sides.
 */
function _positionTooltip (hit) {
  const tooltip = _globalTooltip;
  if (!tooltip) return;

  const MARGIN = 8;  // px clearance from viewport edges
  const GAP = 6;  // px gap between cell and tooltip

  const cell = hit.getBoundingClientRect();
  const tw = tooltip.offsetWidth || 220;
  const th = tooltip.offsetHeight || 140;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Hide if the anchor cell is fully scrolled out of the viewport
  if (cell.bottom < 0 || cell.top > vh || cell.right < 0 || cell.left > vw) {
    _hideTooltip();
    return;
  }

  // Anchor = centre of cell
  const cx = cell.left + cell.width / 2;
  const cy = cell.top + cell.height / 2;

  // Available space in each direction from cell edges
  const spaceBelow = vh - cell.bottom - GAP;
  const spaceAbove = cell.top - GAP;
  const spaceRight = vw - cell.right - GAP;
  const spaceLeft = cell.left - GAP;

  // Prefer vertical axis that has more room
  let x, y;
  if (spaceBelow >= th || spaceBelow >= spaceAbove) {
    y = cell.bottom + GAP;
  } else {
    y = cell.top - GAP - th;
  }

  // Prefer horizontal: centre-align on cell, fall back left/right
  x = cx - tw / 2;
  if (x + tw > vw - MARGIN) x = cell.right - tw; // push left
  if (x < MARGIN) x = cell.left;        // push right

  // If tooltip doesn't fit vertically either side, try horizontal placement
  if (y < MARGIN || y + th > vh - MARGIN) {
    if (spaceRight >= tw) {
      x = cell.right + GAP;
      y = cy - th / 2;
    } else if (spaceLeft >= tw) {
      x = cell.left - GAP - tw;
      y = cy - th / 2;
    }
  }

  // Final viewport clamp
  x = Math.max(MARGIN, Math.min(x, vw - tw - MARGIN));
  y = Math.max(MARGIN, Math.min(y, vh - th - MARGIN));

  tooltip.style.transform = `translate3d(${Math.round(x)}px,${Math.round(y)}px,0)`;
}

// Registers click/tap listeners on hit targets.
// Called after each render — listeners are on SVG elements that are
// replaced each render, so no accumulation.
function _bindTooltip (calendarEl, hitTargets) {
  hitTargets.forEach(hit => {
    // Click toggles — works on desktop and mobile (mobile gets ~300ms delay, acceptable).
    hit.addEventListener('click', e => {
      e.stopPropagation();
      if (_globalTooltipActiveHit === hit) {
        _hideTooltip();
      } else {
        _showTooltip(hit);
      }
    });

  });
}

// ─── RESIZE ──────────────────────────────────────────────────────────────────
// One ResizeObserver per calendar element.
// State is stored in a plain object so _updateResizeState can mutate it
// after each fetch without re-registering the observer.
const _resizeStates = new WeakMap();

function _bindResize (calendarEl) {
  if (_resizeStates.has(calendarEl)) return; // already observing

  const state = {layout: null, data: null, maxProd: 0};
  _resizeStates.set(calendarEl, state);

  const svgContainer = calendarEl.querySelector('[data-calendar-svg]');
  if (!svgContainer) return;

  const ro = new ResizeObserver(() => {
    if (state.layout) {
      _render(calendarEl, state.layout, state.data, state.maxProd);
    }
  });
  ro.observe(svgContainer);
}

function _updateResizeState (calendarEl, layout, data, maxProd) {
  _bindResize(calendarEl); // no-op if already registered
  const state = _resizeStates.get(calendarEl);
  if (state) {
    state.layout = layout;
    state.data = data;
    state.maxProd = maxProd;
  }
}

// ─── STATS ───────────────────────────────────────────────────────────────────
function _updateStats (calendarEl, data) {
  const vals = Object.values(data);
  if (!vals.length) return;

  const total = vals.reduce((s, v) => s + (v.productionReported ?? 0), 0);

  // Nur Tage mit gültigem quota-Wert in den Durchschnitt einbeziehen,
  // um Verzerrung durch null-Tage (keine Produktion, kein Messwert) zu vermeiden.
  const quotaVals = vals.filter(v => v.quota !== null && v.quota !== undefined);
  const quota = quotaVals.length ? quotaVals.reduce((s, v) => s + v.quota, 0) / quotaVals.length : 0;

  _setText(calendarEl, '[data-calendar-stat="total"]', _formatEnergy(total));
  _setText(calendarEl, '[data-calendar-stat="avg"]', `${_formatNumber(total / vals.length, 1)} kWh`);
  _setText(calendarEl, '[data-calendar-stat="quota"]', `${_formatNumber(quota, 0)} %`);
  _setText(calendarEl, '[data-calendar-stat="days"]', `${vals.length}`);
}

// ─── FORMATTERS ──────────────────────────────────────────────────────────────
/**
 * Formats a number in Swiss locale: thousands separator = apostrophe,
 * decimal separator = point.  e.g. 1234.5 -> "1'234.5"
 *
 * Intl.NumberFormat instances are cached by decimal count to avoid
 * repeated construction overhead.
 */
const _numberFormats = new Map();

function _formatNumber (value, decimals) {
  if (!_numberFormats.has(decimals)) {
    _numberFormats.set(decimals, new Intl.NumberFormat('de-CH', {
      minimumFractionDigits: decimals, maximumFractionDigits: decimals,
    }));
  }
  return _numberFormats.get(decimals).format(value);
}

/**
 * Formats an energy value (in kWh) with automatic unit scaling.
 *   < 1 000 kWh  ->  "142.3 kWh"
 *   >= 1 000 kWh ->  "1.42 MWh"
 *   >= 1 000 MWh ->  "1.42 GWh"
 * All numbers formatted in Swiss locale.
 */
function _formatEnergy (kWh) {
  if (kWh >= 1_000_000) return `${_formatNumber(kWh / 1_000_000, 2)} GWh`;
  if (kWh >= 1_000) return `${_formatNumber(kWh / 1_000, 2)} MWh`;
  return `${_formatNumber(kWh, 1)} kWh`;
}

function _setText (root, selector, text) {
  const el = root.querySelector(selector);
  if (el) el.textContent = text;
}

// ─── LEGEND ──────────────────────────────────────────────────────────────────
// Rendered once — the colour scale never changes.
const _legendRendered = new WeakSet();

function _renderLegend (calendarEl) {
  if (_legendRendered.has(calendarEl)) return;

  const legendEl = calendarEl.querySelector('[data-calendar-legend]');
  if (!legendEl) return;

  COLOR_SCALE.forEach(c => {
    const s = document.createElement('span');
    s.className = 'calendar-legend-swatch';
    s.style.background = c;
    legendEl.appendChild(s);
  });

  _legendRendered.add(calendarEl);
}

// ─── COLOR UTILS ─────────────────────────────────────────────────────────────
function _productionColor (value, maxVal) {
  if (!maxVal) return COLOR_SCALE[0];
  const t = Math.min(1, Math.max(0, value / maxVal));
  const idx = t * (COLOR_SCALE.length - 1);
  const lo = Math.floor(idx);
  const hi = Math.min(COLOR_SCALE.length - 1, lo + 1);
  return _blendHex(COLOR_SCALE[lo], COLOR_SCALE[hi], idx - lo);
}

function _blendHex (a, b, t) {
  const c = (hex, o) => parseInt(hex.slice(o, o + 2), 16);
  const r = Math.round(c(a, 1) + (c(b, 1) - c(a, 1)) * t);
  const g = Math.round(c(a, 3) + (c(b, 3) - c(a, 3)) * t);
  const bl = Math.round(c(a, 5) + (c(b, 5) - c(a, 5)) * t);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${bl.toString(16).padStart(2, '0')}`;
}

function _luminance (hex) {
  return 0.299 * parseInt(hex.slice(1, 3), 16) / 255 + 0.587 * parseInt(hex.slice(3, 5), 16) / 255 + 0.114 * parseInt(hex.slice(5, 7), 16) / 255;
}

/**
 * Formats a Date as "YYYY-MM-DD" using local date parts.
 * Intentionally avoids toISOString() which returns UTC — this would produce
 * the wrong date at midnight in UTC+1/+2 (Zürich winter/summer time).
 */
function _formatKey (d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Expected API response shape for GET /calendar.php?date=YYYY-MM-DD :
//
// {
//   "pivot": "2025-03-01",
//   "days": {
//     "2025-01-01": {
//       "productionReported": 58.15,   // SolarManager-Wert in kWh (exakt)
//       "pvToConsumer":       57.04,   // berechnet aus Momentandaten in kWh
//       "pvToGrid":            1.11,   // berechnet aus Momentandaten in kWh
//       "quota":              99.5     // Eigenverbrauch in % (0-100), nullable
//     },
//     ...
//   }
// }
// ─────────────────────────────────────────────────────────────────────────────
